/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { ref, computed, onMounted, watch } from 'vue'
import { useStrapiStore } from '../stores/strapi'
import { useAppStateStore } from '../stores/appState'

const STORAGE_PREFIX = 'myApp_'
const GLOBAL_KEY   = `${STORAGE_PREFIX}globalAuth`

export function useGlobalAuth() {
  const strapiStore                   = useStrapiStore()
  const appStateStore                 = useAppStateStore()
  const isGlobalPasswordAuthenticated = ref(false)
  const showLoading                   = ref(true)
  const hasCheckedSession             = ref(false)
  
  // New loading coordination states
  const animationCompleted            = ref(false)
  const dataLoadingCompleted          = ref(false)
  const isLoadingData                 = ref(false)

  // Check if password is configured
  const isGlobalPasswordRequired = computed(() => {
    return strapiStore.siteInfo?.websitePassword?.passwordProtected === true
  })

  // Computed property to determine if loading is complete
  const isLoadingComplete = computed(() => {
    return animationCompleted.value && dataLoadingCompleted.value
  })

  // Read sessionStorage once, then flip hasCheckedSession & start background loading
  const initializeSession = () => {
    if (isGlobalPasswordRequired.value && sessionStorage.getItem(GLOBAL_KEY) === 'true') {
      isGlobalPasswordAuthenticated.value = true
    }
    hasCheckedSession.value = true
  }

  // Start background data loading
  const startBackgroundLoading = async () => {
    if (isLoadingData.value) return // Prevent duplicate calls
    
    isLoadingData.value = true
    try {
      await strapiStore.initialize()
      dataLoadingCompleted.value = true
      // Notify central state that core data is loaded
      appStateStore.setCoreDataLoaded(true)
    } catch (error) {
      console.error('Background loading failed:', error)
      // Still mark as completed to prevent infinite loading
      dataLoadingCompleted.value = true
      appStateStore.setCoreDataLoaded(true)
    } finally {
      isLoadingData.value = false
    }
  }

  // Whether to show the password gate
  const showPasswordGate = computed(() => {
    // Don't show until we've checked sessionStorage and loading is complete
    if (!hasCheckedSession.value || !isLoadingComplete.value) {
      return false
    }
    return isGlobalPasswordRequired.value && !isGlobalPasswordAuthenticated.value
  })

  // Called by Loading component when animation completes
  const handleAnimationComplete = () => {
    animationCompleted.value = true
    // Notify central state that animation is complete
    appStateStore.setAnimationCompleted(true)
  }



  // Called when user submits correct password
  const handlePasswordCorrect = () => {
    isGlobalPasswordAuthenticated.value = true
    sessionStorage.setItem(GLOBAL_KEY, 'true')
  }

  // Clear auth (for testing/log-out)
  const clearAuth = () => {
    sessionStorage.removeItem(GLOBAL_KEY)
    isGlobalPasswordAuthenticated.value = false
  }

  onMounted(() => {
    initializeSession()
    startBackgroundLoading()
  })

  // Watch for loading completion and auto-hide loading screen
  watch(isLoadingComplete, (newValue) => {
    if (newValue && showLoading.value) {
      // Add a small delay to ensure smooth transition
      setTimeout(() => {
        showLoading.value = false
      }, 300)
    }
  })

  if (typeof window !== 'undefined') {
    ;(window as any).clearAuth = clearAuth
  }

  return {
    showLoading,
    isGlobalPasswordAuthenticated,
    isGlobalPasswordRequired,
    showPasswordGate,
    handleAnimationComplete,
    handlePasswordCorrect,
    clearAuth,
    isLoadingData,
    dataLoadingCompleted,
    animationCompleted,
    isLoadingComplete
  }
}
